// Components
import { ConfigContext, Game, OddsDisplayV2 } from '@velinsi/smp-widgets-ui';
import TailwindTheme from '../../../../../packages/tailwind/tailwind-theme';
import { LinkWrapper } from '../../../../../packages/HOC/LinkWrapper';

// Hooks/Utils
import { useEvent } from '../../../../../packages/hooks/multi-sport/useEvent';
import useOverlapOdds from '../../../../../packages/hooks/useOverlapOdds';
import { useProcessedOdds } from '../../../../../packages/odds/hooks/useProcessedOdds';
import useReconcileScopeMarkets from '../../../../football/src/hooks/useReconcileScopeMarkets';
import useRootNodeRef from '../../../../../packages/hooks/useRootNodeRef';
import { defaultHandleEventClick } from '@sportal365/smp-widget-utils';

// Types
import { BaseWidgetProps, SportTypes } from '../../../../../packages/types';
import { IMultiSportSingleEventConfig } from '../../../../../packages/types/multisport';
import { IThemeCustomization, Themes } from '../../../../../packages/tailwind/types';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import tailwindStyles from '../../styles.css';

export type BasketballWidgetsProps = BaseWidgetProps<IMultiSportSingleEventConfig>;

export const BasketballSingleEventWidget = ({ themes, config, subscribers, shadow, sdkOptions, cuid }: BasketballWidgetsProps) => {
  const { ref } = useRootNodeRef();
  const { oddsMarket, oddsMarketScope } = useReconcileScopeMarkets(config?.dataOddsMarket);

  const {
    handleClick,
    widgetData: { game, isLoading },
  } = useEvent({
    subscribers,
    dataWidgetId: config?.dataWidgetId,
    dataWidgetSport: config?.dataWidgetSport,
    dataRefreshTime: config?.dataRefreshTime,
    idOrSlug: config?.dataMatchId,
    dataOddsDisplay: config?.dataOddsDisplay,
    dataOddsMarket: oddsMarket,
    dataOddsMarketValueType: config?.dataOddsMarketValueType,
    dataOddsPreEventOnly: config?.dataOddsPreEventOnly,
    dataOddsBettingId: config?.dataOddsBettingId,
    dataWidgetCuid: cuid,
    sdkOptions: sdkOptions,
    dataShortStatusType: config?.dataShortStatusType,
    dataTeamNameType: config?.dataTeamNameType,
    dataEntityLinks: config?.dataEntityLinks,
    sport: SportTypes.BASKETBALL,
  });

  const odds = useProcessedOdds({
    odds: game?.odds ?? [],
    dataOddsBettingId: config?.dataOddsBettingId,
    showOdds: config?.dataOddsDisplay,
  });

  const { areBettingOddsOverlapVisible } = useOverlapOdds({ dataBettingLogoOverlap: config?.dataBettingLogoOverlap });

  const handleEventClick = (event: string) => defaultHandleEventClick(handleClick, Number(event));

  if (!game) return null;

  return (
    <ConfigContext.Provider value={{ ...config, sdkOptions }}>
      <TailwindTheme
        isLoading={isLoading}
        shadow={shadow}
        themes={themes ? (themes as IThemeCustomization) : undefined}
        dataTheme={config?.dataTheme ? (config.dataTheme as Themes) : undefined}
        refForShadow={ref}
        tailwindStyles={tailwindStyles}
        bgColor='var(--colors-sportEntityContainerPrimaryBgColor)'
      >
        <LinkWrapper
          url={game?.url}
          target={game?.target}
          shouldWrap={config?.dataWrapperLink}
          onClick={() => game?.id && handleEventClick(game.id)}
        >
          <div className='widget-container p-4'>
            <Game game={game} handleClick={handleClick} showInfo={config?.dataAdditionalInfo} />
            {config?.dataOddsDisplay && odds && (
              <OddsDisplayV2
                fullOdds={odds}
                handleClick={handleClick}
                marketSelectionDisplay={config.dataOddsMarket === undefined}
                isHeader
                isStandalone
                oddsMarketScope={oddsMarketScope}
                areBettingOddsOverlapVisible={areBettingOddsOverlapVisible}
              />
            )}
          </div>
        </LinkWrapper>
      </TailwindTheme>
    </ConfigContext.Provider>
  );
};
